// components/AspectHeatmap.tsx
import React, { useMemo, PropsWithChildren } from "react";
import styles from "./AspectHeatmap.module.scss";

type Review = { content: string; rating: number; date?: string | Date };

type Props = {
  reviews: Review[];
  maxAspects?: number;
  /** 'card' = self-contained card with title; 'bare' = body only (for embedding) */
  variant?: "card" | "bare";
  /** Title when variant='card'. Set false to hide. */
  title?: string | false;
  /** Compact typography & smaller bars when true */
  compact?: boolean;
  className?: string;
};

const ASPECTS: Record<string, string[]> = {
  service: ["service", "serveur", "serveuse", "accueil", "staff", "personnel"],
  delai: ["livraison", "livrer", "retard", "en retard", "délai", "temps", "rapide", "lent"],
  prix: ["prix", "cher", "coût", "coûteux", "bon marché", "tarif"],
  qualite: ["qualité", "qualite", "frais", "propre", "sale", "défectueux", "broken"],
  communication: ["communication", "réponse", "répond", "contact", "support", "email", "téléphone"],
  site: ["site web", "site", "website", "app", "application", "interface", "bug", "lenteur"],
};

function norm(s: string) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") 
    .replace(/[^a-z0-9\s\-']/g, ""); 
}

export default function AspectHeatmap({
  reviews,
  maxAspects = 6,
  variant = "card",
  title = "Thèmes & sentiment",
  compact = false,
  className,
}: Props) {
  const data = useMemo(() => {
    const buckets = Object.keys(ASPECTS).reduce((acc, k) => {
      acc[k] = { aspect: k, mentions: 0, pos: 0, neg: 0 };
      return acc;
    }, {} as Record<string, { aspect: string; mentions: number; pos: number; neg: number }>);

    for (const r of reviews || []) {
      const text = norm(r.content || "");
      if (!text) continue;
      const sentiment = r.rating >= 4 ? 1 : r.rating <= 2 ? -1 : 0;

      for (const [aspect, keys] of Object.entries(ASPECTS)) {
        if (keys.some((k) => text.includes(norm(k)))) {
          buckets[aspect].mentions += 1;
          if (sentiment > 0) buckets[aspect].pos += 1;
          else if (sentiment < 0) buckets[aspect].neg += 1;
        }
      }
    }

    const rows = Object.values(buckets)
      .filter((b) => b.mentions > 0)
      .map((b) => {
        const posPct = b.mentions ? b.pos / b.mentions : 0;
        const negPct = b.mentions ? b.neg / b.mentions : 0;
        return { ...b, posPct, negPct };
      })
      .sort((a, b) => b.mentions - a.mentions)
      .slice(0, maxAspects);

    return rows;
  }, [reviews, maxAspects]);

  const Wrapper = ({ children }: PropsWithChildren) =>
    variant === "card" ? (
      <div className={`${styles.card} ${compact ? styles.compact : ""} ${className || ""}`}>
        {title !== false && <h3>{title}</h3>}
        {children}
      </div>
    ) : (
      <div className={`${compact ? styles.compact : ""} ${className || ""}`}>{children}</div>
    );

  if (!data.length) {
    return (
      <Wrapper>
        <div className={styles.empty}>Pas assez de données pour le moment.</div>
      </Wrapper>
    );
  }

  const labelMap: Record<string, string> = {
    service: "Service",
    delai: "Délai/Livraison",
    prix: "Prix",
    qualite: "Qualité",
    communication: "Communication",
    site: "Site/App",
  };

  return (
    <Wrapper>
      <div className={styles.table}>
        {data.map((row) => (
          <div key={row.aspect} className={styles.row}>
            <div className={styles.left}>
              <span className={styles.aspect}>{labelMap[row.aspect] ?? row.aspect}</span>
              <span className={styles.chip}>{row.mentions}</span>
            </div>
            <div
              className={styles.barWrap}
              title={`Négatif ${Math.round(row.negPct * 100)}% / Positif ${Math.round(row.posPct * 100)}%`}
            >
              <div className={styles.bar}>
                <span className={styles.neg} style={{ width: `${Math.round(row.negPct * 100)}%` }} />
                <span className={styles.pos} style={{ width: `${Math.round(row.posPct * 100)}%` }} />
              </div>
              <div className={styles.legend}>
                <span className={styles.negDot} /> Négatif {Math.round(row.negPct * 100)}%
                <span className={styles.sep}>·</span>
                <span className={styles.posDot} /> Positif {Math.round(row.posPct * 100)}%
              </div>
            </div>
          </div>
        ))}
      </div>
      {variant === "card" && !compact && (
        <p className={styles.note}>
          Basé sur les mentions de mots-clés par thème (heuristique simple). Les notes 4–5★ comptent comme
          positives, 1–2★ comme négatives, 3★ neutre.
        </p>
      )}
    </Wrapper>
  );
}
